import "react-datepicker/dist/react-datepicker.css";
import { dt } from "@resamare/functions";

import fr from "date-fns/locale/fr";

import { useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { FormHelperText } from "../_partials";
import { Day, Header, Input } from "./_partials";
import { DatePortal } from "./_partials/DatePortal";
import styles from "./DateField.module.scss";
import { useMonthsShown } from "./useMonthsShown";

export interface DateFieldProps {
    availableDates?: Date[];
    blockedDates?: Date[];
    dayInfo?: Record<string, string | number>;
    excludeDates?: Date[];
    helperText?: string;
    inline?: boolean;
    inputRef?: React.Ref<HTMLInputElement>;
    isError?: boolean;
    isMonthsLoading?: boolean[];
    label: string;
    locale?: "fr" | "en";
    maxDate?: Date | null;
    minDate?: Date | null;
    name: string;
    onChange: (date: Date | null) => void;
    /**
     * Callback appelé lorsque les mois affichés dans le calendrier changent
     * @param months - Liste des mois au format ISO YYYY-MM
     */
    onMonthsChange?: (months: string[]) => void;
    placeholder?: string;
    portalId?: string;
    value: Date | null;
}

registerLocale("fr", fr);

export function DateField({
    availableDates,
    blockedDates,
    dayInfo,
    excludeDates,
    helperText,
    inline = false,
    inputRef,
    isError,
    isMonthsLoading,
    label,
    locale = "fr",
    maxDate,
    minDate,
    name,
    onChange,
    onMonthsChange,
    placeholder,
    portalId,
    value,
}: DateFieldProps) {
    const { ref: calendarContainerRef, monthsShownLength } = useMonthsShown(inline);

    const handleTouchStart = useCallback((e: TouchEvent) => e.stopPropagation(), []);

    const handleCalendarOpen = () => {
        document.addEventListener("touchstart", handleTouchStart, true);
    };

    const handleCalendarClose = () => {
        document.removeEventListener("touchstart", handleTouchStart, true);
    };

    const renderDayContents = useCallback(
        (day: number, date: Date): JSX.Element => {
            if (!dayInfo) return <Day day={day} />;

            const formattedDate = dt.toIsoDate(date);
            const info = dayInfo[formattedDate];
            return <Day day={day} info={info} size="large" />;
        },
        [dayInfo],
    );

    return (
        <div ref={calendarContainerRef}>
            <DatePicker
                calendarClassName={styles.calendar}
                customInput={
                    <Input inputName={name} inputRef={inputRef} isError={isError} label={label} />
                }
                dateFormat="EEEE d MMM"
                excludeDates={excludeDates}
                formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 2)}
                highlightDates={[
                    { available: availableDates || [] },
                    { blocked: blockedDates || [] },
                ]}
                includeDates={availableDates}
                inline={inline}
                locale={locale}
                maxDate={maxDate || undefined}
                minDate={minDate || undefined}
                monthsShown={monthsShownLength}
                onCalendarClose={handleCalendarClose}
                onCalendarOpen={handleCalendarOpen}
                onChange={onChange}
                placeholderText={placeholder}
                portalId={portalId || "resamare-calendar-root"}
                renderCustomHeader={(params) => (
                    <Header
                        {...params}
                        isMonthsLoading={isMonthsLoading}
                        locale={locale}
                        monthsShownLength={monthsShownLength}
                        onMonthsChange={onMonthsChange}
                    />
                )}
                renderDayContents={renderDayContents}
                selected={value}
                weekDayClassName={() => styles["weekday"]}
                withPortal={!inline}
            />
            {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
            {!portalId ? <DatePortal /> : null}
        </div>
    );
}
